import Button from '@atlaskit/button';
import AddIcon from '@atlaskit/icon/core/migration/add';
import type { FileIdentifier } from '@atlaskit/media-client';
import { FileState } from '@atlaskit/media-state';
import { Box, Flex, Inline, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { pasteContentExperience } from '@databases/frontend-metrics';
import { messages as importMessages } from '@databases/import/messages';
import { usePasteListener } from '@databases/import/usePasteListener';
import { messages } from '@databases/intl/messages';
import { defaultStoreUtils } from '@databases/redux';
import {
  selectIsValueSelected,
  selectSelectedValuesCount,
} from '@databases/redux/cellSelections/selectors';
import { DatabaseFieldType, RawDatabaseValue } from '@databases/types/database';
import { MultiValueChangeType } from '@databases/utils/database/changesets';
import { showErrorSnackbar } from '@databases/utils/errors';
import { getMediaCollectionId } from '@databases/utils/media-client';
import { generateId } from '@databases/utils/utils';
import classNames from 'classnames';
import React, {
  forwardRef,
  FunctionComponent,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Dropzone, { DropzoneRef, useDropzone } from 'react-dropzone';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

import { useMediaClient } from '../../../media-client';
import AttachmentViewer from '../../AttachmentViewer';
import EditableList, { EditableListProps } from '../../EditableList';
import { EditComponentProps } from '../fieldTypes';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 100%;
  min-height: 36px;
  position: relative;
  height: 100%;

  > div {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    flex-grow: 1;
  }

  * {
    // Set the outline color to the same color as the border color
    outline-color: ${colors.B100};
    outline-offset: 1px;
  }

  &:hover {
    div {
      opacity: 1;
    }
  }
`;

const getBoxStyles = (isDragActive: boolean) => {
  return xcss({
    display: 'flex',
    flexDirection: 'row',
    flexGrow: 1,
    alignItems: 'center',

    width: `calc(100% - ${token('space.075')})`,
    border: `2px dashed ${
      isDragActive ? token('color.background.neutral') : 'transparent'
    }`,
    borderRadius: 'border.radius.100',
    height: `calc(100% - ${token('space.075')})`,
    backgroundColor: isDragActive
      ? 'color.background.accent.blue.subtlest.hovered'
      : undefined,
    transition: `border 0.2s`,
  });
};

type Value = RawDatabaseValue<DatabaseFieldType.ATTACHMENT>[0];

const getId = (value: Value): string => value._id;

const getNewValue = (): Value => ({ fileId: '', _id: generateId() });

interface FileDropZoneType {
  children: React.ReactNode;
  id: string;
  index: number;
  handleChangeFile: (val: Value, i: number) => void;
  onDropNewFile: (file: File, i: number) => Promise<string | undefined>;
  noDrag: boolean;
  handleAddFile: (val: Value) => void;
  endIndex: number;
}

// Dropzone wrapper used for changing and dragging to change individual file
export const FileDropzoneWrapper = forwardRef<DropzoneRef, FileDropZoneType>(
  function FileDropZone(
    {
      children,
      id,
      index,
      handleChangeFile,
      onDropNewFile,
      noDrag,
      handleAddFile,
      endIndex,
    },
    ref
  ) {
    return (
      <Dropzone
        ref={ref}
        noClick
        noDrag={noDrag}
        onDropAccepted={async (acceptedFiles) => {
          // Handle dropping multiple files (change with first file and add remaining)
          const placeholderValue = acceptedFiles.length > 1 ? getNewValue() : null;
          if (placeholderValue) {
            handleAddFile(placeholderValue);
          }
          const promises = acceptedFiles.map(async (file, i) => {
            const fileId = await onDropNewFile(file, i === 0 ? index : endIndex);
            if (!fileId) {
              return;
            }
            if (i === 0) {
              handleChangeFile({ _id: id, fileId }, index);
            } else if (i === 1 && placeholderValue) {
              handleChangeFile({ _id: placeholderValue._id, fileId }, endIndex);
            } else {
              handleAddFile({ _id: generateId(), fileId });
            }
          });
          await Promise.all(promises);
        }}
      >
        {({ getRootProps, getInputProps, isDragActive }) => {
          return (
            <div
              {...getRootProps({
                style: {
                  backgroundColor: isDragActive ? colors.B50 : 'transparent',
                },
              })}
              tabIndex={isDragActive ? 1 : -1}
            >
              <input {...getInputProps()} />
              {children}
            </div>
          );
        }}
      </Dropzone>
    );
  }
);

const AttachmentValueEdit: FunctionComponent<
  EditComponentProps<DatabaseFieldType.ATTACHMENT>
> = ({ contentId, onChange, onBlur, field, entryId, value, isSeamless = false }) => {
  const mediaClient = useMediaClient(contentId);
  const store = defaultStoreUtils.useStore();
  const [uploadOrder, setUploadOrder] = useState<number[]>([]); // Represents indices for value that are uploading
  const { formatMessage } = useIntl();
  const [mediaCollectionId, setMediaCollectionId] = useState<null | string>(null);
  const [isHovering, setIsHovering] = useState(false);
  const length = value.length;

  const valueIsTheOnlyValueSelected = defaultStoreUtils.useEqualSelector((state) => {
    if (selectSelectedValuesCount(state, { contentId }) === 1 && entryId) {
      return selectIsValueSelected(state, { fieldId: field.id, entryId });
    }
    return false;
  });

  const isUploading = useMemo(() => {
    return uploadOrder.length > 0;
  }, [uploadOrder]);

  const onDropNewFile = useCallback(
    async (file: File, index: number) => {
      setUploadOrder((prevIsUploading) => [...prevIsUploading, index]);
      try {
        if (mediaCollectionId) {
          const fileState = await new Promise<
            Extract<FileState, { status: 'processed' | 'failed-processing' }>
          >((resolve, reject) => {
            mediaClient?.file
              .upload({
                content: file,
                name: file.name,
                size: file.size,
                mimeType: file.type,
                collection: mediaCollectionId,
              })
              .subscribe((v) => {
                if (v.status === 'processed' || v.status === 'failed-processing') {
                  resolve(v);
                } else if (v.status === 'error') {
                  console.error(v);
                  reject(new Error(`Error uploading media: ${v.message}`));
                }
              });
          });
          return fileState.id;
        }
      } finally {
        setUploadOrder((prevUploading) => {
          return prevUploading.slice(0, -1);
        });
      }
    },
    [mediaClient?.file, mediaCollectionId]
  );

  // Get media files for MediaViewer (carousel)
  const getMediaFiles = useCallback(() => {
    return value.map((file) => {
      return {
        mediaItemType: 'file',
        id: 'fileId' in file ? file.fileId : '',
        collectionName: mediaCollectionId,
      } as FileIdentifier;
    });
  }, [mediaCollectionId, value]);

  const onItemChange = useCallback(
    (value: Value, index: number, _: null, allValues: Value[]) => {
      onChange(allValues, {
        type: DatabaseFieldType.ATTACHMENT,
        delta: {
          type: MultiValueChangeType.EDIT,
          value,
          index,
        },
      });
    },
    [onChange]
  );

  const onItemAdd = useCallback(
    (value: Value, allValues: Value[]) => {
      onChange(allValues, {
        type: DatabaseFieldType.ATTACHMENT,
        delta: {
          type: MultiValueChangeType.APPEND,
          value: value,
        },
      });
    },
    [onChange]
  );

  const onItemDelete = useCallback(
    (_: Value, index: number, allValues: Value[]) => {
      onChange(allValues, {
        type: DatabaseFieldType.ATTACHMENT,
        delta: {
          type: MultiValueChangeType.REMOVE,
          index,
        },
      });
    },
    [onChange]
  );

  const onItemReorder = useCallback(
    (_: Value, currentIndex: number, newIndex: number, allValues: Value[]) => {
      onChange(allValues, {
        type: DatabaseFieldType.ATTACHMENT,
        delta: {
          type: MultiValueChangeType.REORDER,
          currentIndex,
          newIndex,
        },
      });
    },
    [onChange]
  );

  const handleDelete = useCallback(
    (index: number) => {
      onItemDelete(value[index], index, value);
    },
    [onItemDelete, value]
  );

  const handleChangeFile = useCallback(
    (file: Value, index: number) => {
      onItemChange(file, index, null, value);
    },
    [onItemChange, value]
  );

  const handleAddFile = useCallback(
    (file: Value) => {
      onItemAdd(file, value);
    },
    [onItemAdd, value]
  );

  // Delete values without fileId and not currently uploading (to avoid placeholders showing when unnecessary)
  useEffect(() => {
    value.forEach((val, index) => {
      if (!uploadOrder.length && 'fileId' in val && !val.fileId) {
        onItemDelete(value[index], index, value);
      }
    });
  }, [value, uploadOrder, onItemDelete]);

  const handleDroppedFiles = useCallback(
    (acceptedFiles: File[]) => {
      /**
       * When adding files to a cell with files, create placeholder value so that a spinner can be displayed while uploading.
       * Then call change method to replace the placeholder value.
       */
      const index = value.length; // Take value.length to account for placeholder

      const placeholderValue = getNewValue();
      handleAddFile(placeholderValue);

      acceptedFiles.forEach(async (file: File, i: number) => {
        const fileId = await onDropNewFile(file, index);
        if (fileId) {
          const attachmentValue = {
            _id: value[index]?._id ?? generateId(),
            fileId,
          };

          if (i < 1) {
            handleChangeFile(attachmentValue, index); // Change the placeholder value
          } else {
            handleAddFile(attachmentValue); // If dropped more than one file, add the rest
          }
        }
      });
    },
    [handleAddFile, handleChangeFile, onDropNewFile, value]
  );

  const { getRootProps, getInputProps, isDragActive, open, acceptedFiles } = useDropzone({
    onFileDialogCancel: () => {
      onBlur?.();
    },
    noClick: true, // Opening the dialog is handled by the "Add files" button
    disabled: uploadOrder.length > 0,
    noDrag: false,
    onDropAccepted: useCallback(
      async (acceptedFiles: File[]) => {
        await handleDroppedFiles(acceptedFiles);
      },
      [handleDroppedFiles]
    ),
  });

  const onPaste = useCallback(
    async (items: DataTransferItemList) => {
      if (!valueIsTheOnlyValueSelected) {
        return;
      }

      if (items.length === 0) {
        return;
      }
      const imageItems = Array.from(items).filter((item) =>
        item.type.startsWith('image/')
      );
      // if no image items are in the clipboard, let the paste listener in DatabaseActions handle the paste
      if (imageItems.length === 0) {
        return;
      }

      try {
        pasteContentExperience.start();

        const firstImageFile = imageItems[0]?.getAsFile();
        if (firstImageFile) {
          await handleDroppedFiles([...acceptedFiles, firstImageFile]);
          pasteContentExperience.success();
          // return early so we don't enter the logic in the paste listener in DatabaseActions
          return;
        }
      } catch (e) {
        showErrorSnackbar(
          'cannot-read-pasted-content',
          formatMessage(
            importMessages['snackbar-error-message.cannot-read-pasted-content']
          )
        );
        pasteContentExperience.failure();
      }
    },
    [valueIsTheOnlyValueSelected, formatMessage, handleDroppedFiles, acceptedFiles]
  );

  usePasteListener({
    onPaste,
  });

  useEffect(() => {
    const getCollectionId = async () => {
      const id = await getMediaCollectionId(store.getState(), contentId);
      if (id) {
        setMediaCollectionId(id);
      }
    };

    getCollectionId();
  }, [contentId, store]);

  // Create ref for each value in order to open value's dropzone and change corresponding file
  const dropzoneRefs: React.MutableRefObject<RefObject<DropzoneRef>[]> = useRef([]);

  const renderField: EditableListProps<Value, null>['renderField'] = useCallback(
    ({ value, i }) => {
      // Ensure the ref exists at the current index
      if (!dropzoneRefs.current[i]) {
        dropzoneRefs.current[i] = React.createRef();
      }

      return (
        <Flex
          justifyContent="center"
          direction="column"
          xcss={xcss({ width: '100%', height: '100%' })}
        >
          <Box xcss={xcss({ width: '100%', display: 'inline-block', height: 'auto' })}>
            <Container className={classNames({ 'is-seamless': isSeamless })}>
              <FileDropzoneWrapper
                ref={dropzoneRefs.current[i]}
                id={value._id}
                index={i}
                handleChangeFile={handleChangeFile}
                onDropNewFile={onDropNewFile}
                noDrag
                handleAddFile={handleAddFile}
                endIndex={length}
              >
                <AttachmentViewer
                  dropzoneRef={dropzoneRefs.current[i]}
                  contentId={contentId}
                  value={value}
                  handleDelete={() => handleDelete(i)}
                  showSpinner={uploadOrder.includes(i)}
                  mediaFiles={getMediaFiles()}
                />
              </FileDropzoneWrapper>
            </Container>
          </Box>
        </Flex>
      );
    },
    [
      isSeamless,
      handleChangeFile,
      onDropNewFile,
      handleAddFile,
      length,
      contentId,
      uploadOrder,
      getMediaFiles,
      handleDelete,
    ]
  );

  return (
    <Box
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        paddingLeft: token('space.025'),
      }}
    >
      <Box {...getRootProps()} xcss={getBoxStyles(isDragActive)}>
        {/** Use dropzone hook for dragging files into entire value cell (only adding) */}
        <div
          style={{
            maxWidth: '100%',
            width: '100%',
            height: `calc(100% - ${token('space.050')}px)`,
          }}
        >
          <input {...getInputProps()} />

          {valueIsTheOnlyValueSelected && length === 0 ? (
            <Inline
              xcss={xcss({
                width: '100%',
                height: '100%',
              })}
            >
              {!isUploading && (
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    open();
                  }}
                  appearance="subtle"
                  iconBefore={<AddIcon label={''} />}
                >
                  <div
                    {...getRootProps({
                      style: {
                        cursor: !value.length ? 'pointer' : 'default',
                      },
                    })}
                  >
                    {formatMessage(messages['media.add-file'], {
                      isMulti: 2, // Multi is always enabled
                    })}
                  </div>
                </Button>
              )}
            </Inline>
          ) : (
            <EditableList<Value, null>
              getId={getId}
              values={value}
              onItemChange={onItemChange}
              onAddNewItem={open}
              onRemoveItem={onItemDelete}
              onReorder={onItemReorder}
              getNewValue={getNewValue}
              renderField={renderField}
              addLabel={formatMessage(messages['media.add-file'], {
                isMulti: 2, // Multi is always enabled
              })}
              noAdd={!isHovering || value.length === 0}
              verticalScroll={true}
              isHovering={isHovering && !isDragActive} // Used to hide delete and drag handle when not hovering
              isReadonly={uploadOrder.length > 0}
            />
          )}
        </div>
      </Box>
    </Box>
  );
};

export default AttachmentValueEdit;
